import React from 'react';
import { COR, FONTE } from './ui';

// =====================================================================
// GRID DE MÊS — o desenho do calendário (7 colunas, uma célula por dia)
// =====================================================================
// Peça puramente visual, sem nenhuma chamada de API: recebe ano/mês e devolve o
// grid montado; QUEM CHAMA decide o que aparece dentro de cada dia, via a render
// prop `renderDia(dia)`.
//
// Nasceu compartilhado de propósito: o mesmo grid é usado pela tela
// /aniversariantes (feriados + eventos + aniversariantes) e pelo modal do ícone
// de calendário (as mesmas três camadas + os compromissos do Teams). Sem isto, o
// grid — cálculo dos dias em branco, marcação do "hoje", cabeçalho da semana —
// nasceria copiado nos dois lugares.
//
// Props:
//   - ano, mes    : mês exibido (mes 1..12)
//   - renderDia   : (dia:number) => ReactNode — as tags daquele dia
//   - destaqueDia : (dia:number) => cor de fundo | null — pinta a célula (ex.:
//                   azul claro em dia de feriado). Opcional.
//   - onClicarDia : (dia:number) => void — clique na célula. Opcional; quando
//                   passado, a célula ganha cursor de mão.
//   - alturaCelula: altura FIXA da célula (o modal é mais baixo que a tela cheia).
//                   Fixa, não mínima: é o que mantém todo o mês no mesmo padrão —
//                   o dia cheio rola por dentro em vez de esticar a linha.

const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function GridMes({ ano, mes, renderDia, destaqueDia, onClicarDia, alturaCelula = '104px' }) {
    const diasNoMes = new Date(ano, mes, 0).getDate();

    // Células: vazias antes do dia 1 (para o dia cair na coluna certa) + os dias.
    const primeiroDiaSemana = new Date(ano, mes - 1, 1).getDay();   // 0=Dom
    const celulas = [];
    for (let i = 0; i < primeiroDiaSemana; i++) celulas.push(null);
    for (let d = 1; d <= diasNoMes; d++) celulas.push(d);

    const hoje = new Date();
    const mesAtual = mes === hoje.getMonth() + 1 && ano === hoje.getFullYear();
    const diaHoje = hoje.getDate();

    return (
        <div style={{ fontFamily: FONTE }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px', marginBottom: '8px' }}>
                {DIAS_SEMANA.map((d) => (
                    <div key={d} style={{ textAlign: 'center', fontSize: '12px', fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{d}</div>
                ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px' }}>
                {celulas.map((dia, i) => {
                    if (dia === null) return <div key={`v${i}`} />;
                    const ehHoje = mesAtual && dia === diaHoje;
                    // "Hoje" ganha a borda amarela e vence qualquer destaque de fundo.
                    const fundo = ehHoje ? '#fffdf5' : (destaqueDia?.(dia) || '#fff');
                    return (
                        <div
                            key={dia}
                            onClick={onClicarDia ? () => onClicarDia(dia) : undefined}
                            style={{
                                // Altura FIXA (não minHeight): o mês inteiro fica no mesmo
                                // padrão. Dia cheio não estica a linha — a lista interna rola.
                                height: alturaCelula, borderRadius: '12px', padding: '8px',
                                border: ehHoje ? `2px solid ${COR.amarelo}` : `1px solid ${COR.bordaSuave}`,
                                backgroundColor: fundo,
                                // borda + padding entram na altura — sem isto, a célula do
                                // "hoje" (borda 2px) ficaria 2px mais alta que as vizinhas.
                                boxSizing: 'border-box',
                                display: 'flex', flexDirection: 'column', gap: '4px',
                                cursor: onClicarDia ? 'pointer' : 'default',
                            }}
                        >
                            <div style={{ fontSize: '13px', fontWeight: 700, flexShrink: 0, color: ehHoje ? COR.vermelhoEscuro : '#888' }}>
                                {String(dia).padStart(2, '0')}
                            </div>
                            {/* `flex: 1` + `minHeight: 0` é o que faz a rolagem funcionar dentro
                                de um flex container de altura fixa (sem o minHeight, o filho se
                                recusa a encolher e vaza da célula). */}
                            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: '3px', overflowY: 'auto' }}>
                                {renderDia(dia)}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// Tag de um item dentro da célula do dia (feriado, evento, compromisso…).
// Exportada porque as duas telas pintam tags no mesmo formato, só mudando a cor.
export function TagDia({ cor, fundo, texto, title, onClick, icone }) {
    return (
        <div
            onClick={onClick ? (e) => { e.stopPropagation(); onClick(); } : undefined}
            title={title || texto}
            style={{
                backgroundColor: fundo, color: cor, borderRadius: '6px', padding: '3px 6px',
                fontSize: '11px', fontWeight: 700, lineHeight: 1.25,
                // Nome longo (feriado/evento) QUEBRA em vez de virar reticências: nada
                // fica escondido. A célula tem altura fixa, então quem absorve a tag
                // alta é a rolagem interna do dia.
                whiteSpace: 'normal', overflowWrap: 'anywhere',
                cursor: onClick ? 'pointer' : 'default', flexShrink: 0,
                display: 'flex', alignItems: 'flex-start', gap: '4px', fontFamily: FONTE,
            }}
        >
            {icone}
            <span style={{ minWidth: 0 }}>{texto}</span>
        </div>
    );
}

export default GridMes;
